<template>
  <LoadingSpinner :loading='loading'> </LoadingSpinner>

  <div class="container mt-4">
    <h3 class="mb-1">Abonnement og betaling</h3>
    <div style="font-style:italic; color:var(--bo-muted); max-width:680px" class="mb-4">
      CAGISTA fakturerer <strong>etterskuddsvis</strong>: trekket i begynnelsen av en måned dekker måneden
      som er gått — lisensen og SMS-forbruket i den. Sier du opp midt i en måned, avkortes lisensen til
      dagene du har brukt. Prøveperioden er gratis, og starter først den dagen
      nettbutikken din kan ta imot bestillinger — ikke når du registrerte deg. Legg til et betalingskort
      før den er over for å fortsette uten avbrudd. Kortet håndteres trygt hos {{ pspName }}; vi lagrer
      aldri kortnummeret.
    </div>

    <div style="max-width:680px">
      <div class="billing-card" :class="statusClass">
        <div class="billing-status-row">
          <span class="billing-badge">{{ statusLabel }}</span>
          <span v-if="message" class="billing-msg">{{ message }}</span>
        </div>

        <!-- Contextual lines -->
        <!-- Before the trial: no countdown, because none has started. The promise is the point — a merchant
             who thinks the clock is running while they build their menu rushes the menu. -->
        <div v-if="status.status === 'onboarding'" class="mt-2" style="color:var(--bo-ink-2)">
          De {{ status.trialDays || 30 }} gratis dagene dine starter den dagen nettbutikken kan ta imot
          bestillinger — ikke nå. Du betaler ingenting mens du setter opp.
          <ul class="billing-todo mt-2">
            <li :class="{ 'billing-todo--done': readiness.menu }">
              <i :class="readiness.menu ? 'fas fa-check' : 'far fa-circle'"></i>
              <span>Legg inn menyen</span>
              <router-link v-if="!readiness.menu" to="/products">Gå til Produkter</router-link>
            </li>
            <li :class="{ 'billing-todo--done': readiness.payment }">
              <i :class="readiness.payment ? 'fas fa-check' : 'far fa-circle'"></i>
              <span>Aktiver betaling med VIVA</span>
              <router-link v-if="!readiness.payment" to="/vivaOnboarding">Aktiver VIVA</router-link>
            </li>
          </ul>
        </div>
        <!-- The free tier. Nothing is counting and nothing is owed, so the page says exactly that —
             a merchant who suspects a bill is coming will not build anything on the platform. The same
             two steps are listed, as an offer rather than a deadline. -->
        <div v-if="status.status === 'free'" class="mt-2" style="color:var(--bo-ink-2)">
          Du har gratis hjemmeside hos oss. Den koster ingenting, og det er ingen prøveperiode som
          løper ut.
          <p class="mt-2 mb-1">Vil du også ta imot bestillinger på nett? Da starter de
            {{ status.trialDays || 30 }} gratis dagene dine først når nettbutikken er klar:</p>
          <ul class="billing-todo mt-2">
            <li :class="{ 'billing-todo--done': readiness.menu }">
              <i :class="readiness.menu ? 'fas fa-check' : 'far fa-circle'"></i>
              <span>Legg inn menyen</span>
              <router-link v-if="!readiness.menu" to="/products">Gå til Produkter</router-link>
            </li>
            <li :class="{ 'billing-todo--done': readiness.payment }">
              <i :class="readiness.payment ? 'fas fa-check' : 'far fa-circle'"></i>
              <span>Aktiver betaling med VIVA</span>
              <router-link v-if="!readiness.payment" to="/vivaOnboarding">Aktiver VIVA</router-link>
            </li>
          </ul>
        </div>
        <div v-if="status.status === 'trialing'" class="mt-2" style="color:#663c00">
          Prøveperioden utløper {{ formatDate(status.trialEnd) }}. Legg til et betalingskort for å fortsette.
        </div>
        <div v-if="status.status === 'past_due'" class="mt-2" style="color:#663c00">
          Siste betaling feilet — oppdater betalingskortet for å unngå at tjenesten settes på pause.
        </div>
        <div v-if="status.status === 'expired' || status.status === 'unpaid'" class="mt-2" style="color:#b91c1c">
          Tjenesten er satt på pause. Legg til / oppdater betalingskortet for å gjenoppta.
        </div>
        <div v-if="status.status === 'canceled'" class="mt-2" style="color:var(--bo-ink-2)">
          Abonnementet er avsluttet. Legg til et betalingskort for å starte på nytt.
        </div>

        <div v-if="status.hasCard" class="mt-2" style="color:#2f7a3a">
          Betalingskort: {{ status.card.brand }} •••• {{ status.card.last4 }}.
        </div>

        <!-- Derived plan (from the merchant's terminals + locations) -->
        <div v-if="status.modules && status.modules.length" class="billing-plan mt-3">
          <div class="billing-plan__title">Din plan</div>
          <div v-for="m in status.modules" :key="m.key" class="billing-plan__row">
            <span>{{ m.qty }}× {{ moduleLabel(m.key) }} <span class="billing-plan__unit">(kr {{ kr(m.unitMinor) }}/stk)</span></span>
            <span>kr {{ kr(m.unitMinor * m.qty) }}</span>
          </div>
          <!-- kr 0 for a module reads as free. It is not free, it is unpriced — and until it is priced
               nothing is charged at all, which support needs to be able to see from this page. -->
          <div v-if="status.unpricedModules && status.unpricedModules.length" class="billing-plan__row billing-plan__unpriced">
            <span>Mangler pris: {{ status.unpricedModules.join(', ') }} — ingen trekk gjøres før den er satt.</span>
          </div>
          <div class="billing-plan__row billing-plan__subtotal">
            <span>Total</span>
            <span>kr {{ kr(status.monthlyExMinor) }}</span>
          </div>
          <div class="billing-plan__row">
            <span>Mva (25 %)</span>
            <span>kr {{ kr((status.monthlyGrossMinor || 0) - (status.monthlyExMinor || 0)) }}</span>
          </div>
          <div class="billing-plan__row billing-plan__total">
            <span>Per måned (inkl. mva)</span>
            <span>kr {{ kr(status.monthlyGrossMinor) }}</span>
          </div>
        </div>
        <div v-if="pendingCancel" class="mt-1" style="color:#663c00">
          Abonnementet avsluttes {{ formatDate(status.currentPeriodEnd) }} — ingen flere trekk etter det.
        </div>
        <div v-else-if="status.status === 'active' && nextChargeDate" class="mt-1" style="color:var(--bo-ink-2)">
          Neste trekk: {{ nextChargeDate }}.
        </div>

        <!-- Actions -->
        <div class="d-flex align-items-center mt-3" style="gap:0.75rem; flex-wrap:wrap">
          <button class="bo_btn" :disabled="starting" @click="addCard">
            {{ starting ? 'Starter…' : (status.hasCard ? 'Bytt kort' : 'Legg til betalingskort') }}
          </button>
          <button v-if="pendingCancel" class="bo_btn" :disabled="mutating" @click="doResume">
            {{ mutating ? 'Lagrer…' : 'Angre oppsigelse' }}
          </button>
          <button v-else-if="canCancel" class="bo_btn_secondary" :disabled="mutating" @click="cancelConfirming = true">
            Si opp abonnement
          </button>
          <button class="bo_btn_secondary" :disabled="loading" @click="loadStatus">Oppdater status</button>
        </div>

        <!-- Inline cancel confirmation -->
        <div v-if="cancelConfirming" class="billing-confirm mt-3">
          <!-- What actually happens, in the order it happens: the meter stops today, you are billed for the
               days you had, and you keep getting in. Anything vaguer here is a support call later. -->
          <span>
            Si opp abonnementet? Faktureringen stopper i dag, og sluttfakturaen dekker bare dagene du har
            brukt denne måneden — ikke resten av den. Du beholder tilgangen etterpå, så du får gjort opp
            ordrer og tatt ut rapporter.
          </span>
          <div class="d-flex mt-2" style="gap:0.5rem">
            <button class="bo_btn" :disabled="mutating" @click="doCancel">{{ mutating ? 'Lagrer…' : 'Ja, si opp' }}</button>
            <button class="bo_btn_secondary" :disabled="mutating" @click="cancelConfirming = false">Avbryt</button>
          </div>
        </div>
      </div>

      <!-- The bilag for each charge, so the owner or the accountant need not dig in the e-mail. -->
      <div class="billing-card mt-3">
        <SubscriptionReceipts title="Kvitteringer" />
      </div>
    </div>
  </div>
</template>

<script>
import LoadingSpinner from "@/components/other/LoadingSpinner";
import SubscriptionService from "@/services/site/SubscriptionService";
import SubscriptionReceipts from "@/components/billing/SubscriptionReceipts.vue";

export default {
  name: "Billing",
  components: { LoadingSpinner, SubscriptionReceipts },
  data() {
    return {
      loading: true,
      starting: false,
      mutating: false,
      cancelConfirming: false,
      status: { status: '', trialEnd: '', hasCard: false, card: { last4: '', brand: '' }, cancelAtPeriodEnd: false, currentPeriodEnd: '', nextChargeEpoch: 0, modules: [], monthlyExMinor: 0, monthlyGrossMinor: 0, psp: '' },
      message: ''
    };
  },
  computed: {
    statusLabel() {
      const map = {
        onboarding: 'Under oppsett',
        free: 'Gratis hjemmeside',
        trialing: 'Prøveperiode',
        trialing_carded: 'Prøveperiode (kort registrert)',
        active: 'Aktivt abonnement',
        past_due: 'Betaling feilet',
        unpaid: 'Satt på pause',
        expired: 'Prøveperiode utløpt',
        canceled: 'Avsluttet'
      };
      return map[this.status.status] || this.status.status;
    },
    readiness() {
      return this.status.readiness || { menu: false, payment: false };
    },
    statusClass() {
      const s = this.status.status;
      if (s === 'active' || s === 'trialing_carded' || s === 'free') return 'billing-card--active';
      if (s === 'past_due') return 'billing-card--warn';
      if (s === 'unpaid' || s === 'expired' || s === 'canceled') return 'billing-card--urgent';
      return '';
    },
    pendingCancel() {
      return this.status.cancelAtPeriodEnd && this.status.status !== 'canceled';
    },
    canCancel() {
      return this.status.hasCard && ['trialing_carded', 'active', 'past_due'].includes(this.status.status);
    },
    /**
     * Who actually holds the card, from the server.
     *
     * This sentence named Dintero in hardcoded text, and stopped being true the day billingPsp became a
     * setting — telling the merchant the wrong company handles their card details, on the one page whose
     * job is to reassure them about exactly that. Falls back to the neutral "betalingsleverandøren vår"
     * rather than guessing a name, so an older backend that does not send `psp` says something true
     * instead of something specific and wrong.
     */
    pspName() {
      const names = { VIVA: 'Viva', SWEDBANK_PAY: 'Swedbank Pay', DINTERO: 'Dintero' };
      return names[this.status.psp] || 'betalingsleverandøren vår';
    },
    nextChargeDate() {
      if (!this.status.nextChargeEpoch) return '';
      const d = new Date(this.status.nextChargeEpoch);
      return isNaN(d.getTime()) ? '' : d.toLocaleDateString('nb-NO', { day: '2-digit', month: '2-digit', year: 'numeric' });
    }
  },
  mounted() {
    const txId = this.returnedTransactionId();
    if (txId) {
      this.confirmReturn(txId);
    } else {
      this.loadStatus();
    }
  },
  methods: {
    /**
     * The transaction id a PSP appended to this return URL — under whichever name it uses.
     *
     * Dintero and Swedbank Pay say `transaction_id` / `transactionId`. **Viva says `t`**, with `s` for the
     * order code, and it does not promise the case. Reading only the first two meant that with Viva
     * selected the card was entered, the merchant landed back here, and nothing captured it — leaving the
     * webhook as the only path, which is the one part of this integration never exercised. A card that
     * silently fails to register looks exactly like a card the merchant forgot to add.
     *
     * Matched case-insensitively for the same reason `/store/vivaPaymentStatus` does it: the Storefront
     * was bitten by Viva varying the case of a redirect parameter.
     */
    returnedTransactionId() {
      const q = this.$route.query || {};
      const find = (name) => {
        const key = Object.keys(q).find((k) => k.toLowerCase() === name.toLowerCase());
        return key ? String(q[key]) : '';
      };
      return find('transaction_id') || find('transactionId') || find('t');
    },
    loadStatus() {
      this.loading = true;
      this.message = '';
      SubscriptionService.getStatus().then(
        (res) => { this.status = res.data || this.status; this.loading = false; },
        (err) => { console.log(err); this.loading = false; }
      );
    },
    confirmReturn(txId) {
      this.loading = true;
      SubscriptionService.confirm(txId).then(
        (res) => {
          this.status = res.data || this.status;
          this.loading = false;
          if (!this.status.hasCard) this.message = 'Kortregistreringen ble ikke fullført. Prøv igjen.';
          this.$router.replace({ query: {} }).catch(() => {});
        },
        (err) => { console.log(err); this.loading = false; this.loadStatus(); }
      );
    },
    addCard() {
      if (this.starting) return;
      this.starting = true;
      this.message = '';
      SubscriptionService.startVault().then(
        (res) => {
          const data = res.data || {};
          if (data.checkoutUrl) {
            window.location.href = data.checkoutUrl; // redirect to Dintero's hosted card form
            return;
          }
          if (data.configured === false) {
            this.message = 'Kortbetaling er ikke konfigurert i systemet ennå. Kontakt CAGISTA.';
          } else if (data.error) {
            this.message = data.error;
          }
          this.starting = false;
        },
        (err) => {
          console.log(err);
          this.message = 'Kunne ikke starte kortregistrering. Prøv igjen.';
          this.starting = false;
        }
      );
    },
    doCancel() {
      if (this.mutating) return;
      this.mutating = true;
      SubscriptionService.cancel().then(
        (res) => { this.status = res.data || this.status; this.mutating = false; this.cancelConfirming = false; },
        (err) => { console.log(err); this.message = 'Kunne ikke si opp. Prøv igjen.'; this.mutating = false; }
      );
    },
    doResume() {
      if (this.mutating) return;
      this.mutating = true;
      SubscriptionService.resume().then(
        (res) => { this.status = res.data || this.status; this.mutating = false; },
        (err) => { console.log(err); this.message = 'Kunne ikke angre. Prøv igjen.'; this.mutating = false; }
      );
    },
    formatDate(iso) {
      if (!iso) return '';
      const d = new Date(iso);
      if (isNaN(d.getTime())) return iso;
      return d.toLocaleDateString('nb-NO', { day: '2-digit', month: '2-digit', year: 'numeric' });
    },
    moduleLabel(key) {
      return {
        POS: 'Kassesystem (POS)',
        KIOSK: 'Selvbetjeningskiosk',
        KDS: 'Kjøkkenskjerm (KDS)',
        TAKEAWAY: 'Online takeaway',
        QR: 'QR-bestilling',
        BOOKING: 'Bordbooking',
        SMS: 'SMS sendt'
      }[key] || key;
    },
    kr(minor) {
      // Exact 2 decimals (e.g. "960,50") — billing amounts must be precise, not rounded to whole krone.
      return ((minor || 0) / 100).toLocaleString('nb-NO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
  }
};
</script>

<style scoped>
.billing-card { border: 1px solid var(--bo-line); border-radius: 10px; padding: 16px 18px; background: var(--bo-surface); }
.billing-card--active { background: var(--bo-surface-2); border-color: #b7e2c0; }
.billing-card--warn { background: var(--bo-brand-tint); border-color: #ffd591; }
.billing-card--urgent { background: var(--bo-bad-bg); border-color: #f5a3a3; }
.billing-plan__unpriced { color: var(--bo-alert-ink); font-size: 0.85rem; }
.billing-status-row { display: flex; align-items: center; gap: 0.75rem; flex-wrap: wrap; }
.billing-todo { list-style: none; padding: 0; margin: 0; }
.billing-todo li { display: flex; align-items: center; gap: 0.5rem; padding: 0.25rem 0; }
.billing-todo li i { width: 1rem; color: #adb5bd; }
.billing-todo--done { color: #2f7a3a; }
.billing-todo--done i { color: var(--bo-success-ink); }
.billing-todo a { margin-left: 0.25rem; }
.billing-badge { font-weight: 600; }
.billing-msg { color: #c00; }
.billing-confirm { border-top: 1px solid var(--bo-line); padding-top: 12px; color: #663c00; }
.billing-plan { border-top: 1px solid var(--bo-line); padding-top: 10px; max-width: 360px; }
.billing-plan__title { font-weight: 600; margin-bottom: 4px; }
.billing-plan__row { display: flex; justify-content: space-between; gap: 1rem; font-size: 0.9rem; color: var(--bo-ink-2); padding: 2px 0; }
.billing-plan__unit { color: var(--bo-muted); font-weight: 400; }
.billing-plan__subtotal { border-top: 1px solid var(--bo-line); margin-top: 4px; padding-top: 6px; }
.billing-plan__total { border-top: 1px solid var(--bo-line); margin-top: 4px; padding-top: 6px; font-weight: 600; color: var(--bo-ink); }
</style>
